import { NgOptimizedImage } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { BreadcrumbComponent } from '../../../shared/components/breadcrumb/breadcrumb.component';
import { LucideModule } from '../../../shared/modules/lucide.module';
import { CanalSicPublicoService } from '../services/canal-sic-publico.service';
import { CANAL_SIC_NAO_PUBLICADO, enderecoSic, prazosSic, responsavelSic, telefoneSic } from './prazos-sic';

type CorCanal = 'azul' | 'verde' | 'roxo' | 'laranja';

interface CanalSic {
  id: string;
  titulo: string;
  icone: string;
  cor: CorCanal;
  link: string;
}

type TipoContato = 'telefone' | 'email' | 'texto';

interface ValorContato {
  texto: string;
  tipo: TipoContato;
}

interface InformacaoSic {
  id: string;
  rotulo: string;
  icone: string;
  valores: readonly ValorContato[];
}

const CANAIS: readonly CanalSic[] = [
  {
    id: 'realizar-pedido',
    titulo: 'Realizar pedido',
    icone: 'file-plus-2',
    cor: 'azul',
    link: '/sic/pedido',
  },
  {
    id: 'estatisticas',
    titulo: 'Estatísticas',
    icone: 'chart-bar',
    cor: 'verde',
    link: '/sic/estatisticas',
  },
  {
    id: 'sic-presencial',
    titulo: 'SIC presencial',
    icone: 'map-pin',
    cor: 'roxo',
    link: '/sic/presencial',
  },
  {
    id: 'consulta',
    titulo: 'Consulta',
    icone: 'clipboard-list',
    cor: 'laranja',
    link: '/acompanhamento',
  },
];

@Component({
  selector: 'app-sic',
  templateUrl: './sic.component.html',
  styleUrls: ['./sic.component.css'],
  imports: [BreadcrumbComponent, LucideModule, NgOptimizedImage, RouterLink],
})
export class SicComponent {
  readonly canais = CANAIS;
  readonly naoPublicado = CANAL_SIC_NAO_PUBLICADO;
  private readonly publico = inject(CanalSicPublicoService);

  /** Enquanto a leitura não termina, o bloco não exibe nada — nem dado, nem estado vazio. */
  readonly carregando = this.publico.carregando;
  readonly canal = this.publico.canal;

  /** O nome do responsável do canal, ou "em atualização" — a linha é exibida sempre. */
  readonly responsavel = computed(() => responsavelSic(this.canal()?.responsavel));

  /** Os quatro itens da grade, do canal publicado: local, e-mail, **um** telefone e horário. */
  readonly informacoes = computed<readonly InformacaoSic[]>(() => {
    const canal = this.canal();

    if (!canal) return [];

    const item = (id: string, rotulo: string, icone: string, texto: string | null, tipo: TipoContato): InformacaoSic => ({
      id,
      rotulo,
      icone,
      valores: texto?.trim() ? [{ texto: texto.trim(), tipo }] : [],
    });

    return [
      item('local', 'Local', 'map-pin', enderecoSic(canal.endereco), 'texto'),
      item('email', 'E-mail', 'mail', canal.email, 'email'),
      item('telefone', 'Telefone', 'phone', telefoneSic(canal.telefone), 'telefone'),
      item('horario', 'Horário de funcionamento', 'clock', canal.horarioAtendimento, 'texto'),
    ];
  });

  /** Os três prazos, com o texto pronto do backend na unidade do canal. */
  readonly prazos = computed(() => prazosSic(this.canal()));

  href(valor: ValorContato): string | null {
    switch (valor.tipo) {
      case 'telefone':
        return `tel:${valor.texto.replace(/\D/g, '')}`;
      case 'email':
        return `mailto:${valor.texto}`;
      default:
        return null;
    }
  }
}
